import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { api, fmt } from "@/lib/api";

const TypeTag = ({ type }) =>
  type === "credit" ? (
    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700">
      CREDIT
    </span>
  ) : (
    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-rose-100 text-rose-700">
      DEBIT
    </span>
  );

export const TransactionsTable = ({ transactions, categories, period, onChange }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [newTxn, setNewTxn] = useState({
    date: "",
    description: "",
    amount: "",
    type: "debit",
    category: "Other Expense",
  });

  const catsFor = (type) => (type === "credit" ? categories.income : categories.expense);

  const patch = async (id, payload) => {
    try {
      await api.update(id, payload);
      if (payload.category) {
        // Save as a rule and auto-apply to matching charges across all months
        const res = await api.recategorize("");
        if (res.updated > 1) {
          toast.success(`Saved rule — auto-applied to ${res.updated} matching charges`);
        } else {
          toast.success("Category saved — future matching charges will auto-categorize");
        }
      }
      onChange?.();
    } catch {
      toast.error("Update failed");
    }
  };

  const changeType = async (t, type) => {
    const defaultCat = type === "credit" ? "Other Income" : "Other Expense";
    await patch(t.id, { type, category: defaultCat });
  };

  const del = async (id) => {
    try {
      await api.remove(id);
      toast.success("Transaction removed");
      onChange?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const addTxn = async () => {
    if (!newTxn.description || !newTxn.amount) {
      toast.error("Description and amount are required");
      return;
    }
    try {
      await api.create({ ...newTxn, amount: parseFloat(newTxn.amount), period: period || "" });
      toast.success("Transaction added");
      setAddOpen(false);
      setNewTxn({ date: "", description: "", amount: "", type: "debit", category: "Other Expense" });
      onChange?.();
    } catch {
      toast.error("Failed to add");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
      data-testid="transactions-table"
    >
      <div className="flex items-center justify-between p-5 border-b border-gray-200">
        <div>
          <h3 className="font-heading text-xl font-bold text-gray-900">Transactions</h3>
          <p className="text-sm text-gray-500">Edit type and category to fine-tune your P&L.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-transaction-button"
              className="bg-black text-white hover:bg-gray-800 hover:text-white"
            >
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-transaction-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Add Transaction</DialogTitle>
              <DialogDescription>Manually record a credit or debit charge for your P&L.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Date</Label>
                <Input
                  data-testid="new-date"
                  value={newTxn.date}
                  onChange={(e) => setNewTxn({ ...newTxn, date: e.target.value })}
                  placeholder="e.g. 2026-06-01"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  data-testid="new-description"
                  value={newTxn.description}
                  onChange={(e) => setNewTxn({ ...newTxn, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount</Label>
                  <Input
                    data-testid="new-amount"
                    type="number"
                    value={newTxn.amount}
                    onChange={(e) => setNewTxn({ ...newTxn, amount: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={newTxn.type}
                    onValueChange={(v) =>
                      setNewTxn({
                        ...newTxn,
                        type: v,
                        category: v === "credit" ? "Other Income" : "Other Expense",
                      })
                    }
                  >
                    <SelectTrigger data-testid="new-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="debit">Debit</SelectItem>
                      <SelectItem value="credit">Credit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={newTxn.category}
                  onValueChange={(v) => setNewTxn({ ...newTxn, category: v })}
                >
                  <SelectTrigger data-testid="new-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {catsFor(newTxn.type).map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="save-transaction-button"
                onClick={addTxn}
                className="bg-black text-white hover:bg-gray-800 hover:text-white"
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-28">Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-28">Type</TableHead>
              <TableHead className="w-56">Category</TableHead>
              <TableHead className="text-right w-32">Amount</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-gray-500 py-10">
                  No transactions yet. Upload a statement to get started.
                </TableCell>
              </TableRow>
            )}
            {transactions.map((t) => (
              <TableRow key={t.id} data-testid={`txn-row-${t.id}`} className="hover:bg-gray-50">
                <TableCell>
                  <Input
                    defaultValue={t.date}
                    onBlur={(e) => e.target.value !== t.date && patch(t.id, { date: e.target.value })}
                    className="h-8 font-mono text-xs border-transparent hover:border-gray-200 focus:border-gray-300"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    defaultValue={t.description}
                    onBlur={(e) =>
                      e.target.value !== t.description && patch(t.id, { description: e.target.value })
                    }
                    className="h-8 text-sm border-transparent hover:border-gray-200 focus:border-gray-300"
                  />
                </TableCell>
                <TableCell>
                  <Select value={t.type} onValueChange={(v) => changeType(t, v)}>
                    <SelectTrigger
                      data-testid={`type-select-${t.id}`}
                      className="h-8 border-transparent hover:border-gray-200 p-1 w-fit"
                    >
                      <TypeTag type={t.type} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credit">Credit</SelectItem>
                      <SelectItem value="debit">Debit</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select value={t.category} onValueChange={(v) => patch(t.id, { category: v })}>
                    <SelectTrigger
                      data-testid={`category-select-${t.id}`}
                      className="h-8 text-sm border-gray-200"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {catsFor(t.type).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <Input
                    type="number"
                    defaultValue={t.amount}
                    onBlur={(e) =>
                      parseFloat(e.target.value) !== t.amount &&
                      patch(t.id, { amount: parseFloat(e.target.value) })
                    }
                    className={`h-8 font-mono text-sm text-right border-transparent hover:border-gray-200 focus:border-gray-300 ${
                      t.type === "credit" ? "text-emerald-600" : "text-rose-600"
                    }`}
                  />
                </TableCell>
                <TableCell>
                  <button
                    data-testid={`delete-txn-${t.id}`}
                    onClick={() => del(t.id)}
                    className="text-gray-400 hover:text-rose-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </motion.div>
  );
};
